/**
 * The v2 top bar (TMMAPP-0150 U2 §B 10, visual contract v2 §3, P:76–86) on every shell page: on the
 * page canvas with a 1 px `line` rule under it, padding 14 × 32 (16 at `xs`). At its start, the
 * menu button below `lg` (it opens the sidebar drawer). The bar carries no privacy pill on any page
 * (TMMAPP-0161 item 3, Operator 2026-10-20). At its end: the staging badge and the support chip
 * (D1), Search transactions for an owner (D1: it moves into My Accounts in TMMAPP-0151), today's
 * date (hidden at `xs`), and the people present — your avatar, which opens the account menu, then
 * the route's shared space's people when the data carries them (32 px, a 2 px `bg` ring,
 * overlapping by 8). The page title is the page's (`PageHeader`); the bar shows none.
 */

import { Avatar, Box, ButtonBase, IconButton, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Me } from '@/api/me';
import { DeployEnvBadge } from '@/components/DeployEnvBadge';
import { KindChip } from '@/components/KindChip';
import { MenuIcon, SearchIcon } from '@/components/icons';
import { formatCivilDate } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { resolveTokens } from '@/theme';
import { tintFor } from '@/theme/categorical';
import { initialOf } from './initials';
import { routeVirtualAccountId } from './privacy';

const AVATAR = 32;

/** Today in the person's zone, through `format.ts` ("Sat, Oct 17"); null when it can't be read. */
function useToday(me: Me): { civil: string; text: string } | null {
  const locale = useLocale();
  try {
    const civil = todayCivilInZone(me.user.timezone ?? 'UTC');
    const text = formatCivilDate(civil, locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return { civil, text };
  } catch {
    return null;
  }
}

interface TopBarProps {
  me: Me;
  /** `lg` and up: the sidebar is permanent and there is no menu button. */
  wide: boolean;
  navOpen: boolean;
  onOpenNav: () => void;
  menuOpen: boolean;
  onOpenMenu: (anchor: HTMLElement) => void;
}

export function TopBar({ me, wide, navOpen, onOpenNav, menuOpen, onOpenMenu }: TopBarProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const today = useToday(me);
  const searchGate = useSubscriberAction(() => {
    void navigate('/me/search');
  });

  // The space's people, when the data carries them: the owner of a space shared with you, or the
  // people one of yours is shared with — the route's virtual account on a `/v/…` route.
  const routeId = routeVirtualAccountId(pathname);
  const routeSpace =
    routeId === null ? undefined : me.virtualAccounts.find((entry) => entry.id === routeId);
  const spaceOwner = (owner: string | null) => (owner === null ? [] : [owner]);
  const people: string[] =
    routeSpace === undefined
      ? []
      : routeSpace.mine
        ? routeSpace.sharedWithNames
        : spaceOwner(routeSpace.ownerDisplayName ?? routeSpace.sharedBy);
  const ring = { border: '2px solid', borderColor: tokens.bg, boxSizing: 'content-box' } as const;
  const peach = tintFor('peach', tokens);

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: 'flex',
        alignItems: 'center',
        // One row at every width (TMMAPP-0154 §A 4): the date and (at `xs`) the
        // staging badge give way, and the avatar stays on the row.
        flexWrap: 'nowrap',
        gap: 1.5,
        px: { xs: 2, sm: 4 },
        py: '14px',
        bgcolor: tokens.bg,
        color: tokens.text,
        borderBottom: '1px solid',
        borderColor: tokens.line,
      }}
    >
      {!wide && (
        <IconButton
          aria-label={t('shell.openNavigation')}
          aria-expanded={navOpen}
          onClick={onOpenNav}
          sx={{ width: 40, height: 40, marginInlineStart: -1, color: tokens.text }}
        >
          <MenuIcon />
        </IconButton>
      )}
      <Box sx={{ flex: 1 }} />
      {/* At `xs` the staging badge moves into the account menu (TMMAPP-0154 §A 4). */}
      <Box data-bar-env="" sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}>
        <DeployEnvBadge />
      </Box>
      {me.principalType === 'PLATFORM_SUPPORT' && (
        <KindChip neutral label={t('session.platformSupport')} />
      )}
      {me.tier !== 'GUEST' && (
        <>
          <IconButton
            aria-label={t('shell.searchTransactions')}
            title={t('shell.searchTransactions')}
            aria-current={pathname === '/me/search' ? 'page' : undefined}
            aria-describedby={searchGate.descriptionId}
            onClick={searchGate.run}
            sx={{ width: 36, height: 36, color: tokens.text2 }}
          >
            <SearchIcon size={18} />
          </IconButton>
          <SubscriberActionHint
            id={searchGate.descriptionId}
            description={searchGate.description}
          />
        </>
      )}
      {today !== null && (
        <Box
          component="time"
          dateTime={today.civil}
          sx={{
            display: { xs: 'none', sm: 'inline' },
            fontSize: 13,
            lineHeight: '20px',
            color: tokens.textLabel('bg'),
            whiteSpace: 'nowrap',
          }}
        >
          {today.text}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <ButtonBase
          aria-label={t('shell.accountMenu')}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={(event) => onOpenMenu(event.currentTarget)}
          data-account-avatar=""
          sx={{
            borderRadius: '50%',
            '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
          }}
        >
          <Avatar
            aria-hidden
            sx={{
              width: AVATAR,
              height: AVATAR,
              ...ring,
              bgcolor: tokens.brand,
              color: tokens.brandInk,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {initialOf(me.user.displayName)}
          </Avatar>
        </ButtonBase>
        {people.map((name, index) => (
          <Avatar
            key={`${index}:${name}`}
            role="img"
            aria-label={personName(name, t)}
            sx={{
              width: AVATAR,
              height: AVATAR,
              ...ring,
              marginInlineStart: '-8px',
              bgcolor: peach.soft,
              color: tokens.text,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <span aria-hidden>{initialOf(personName(name, t))}</span>
          </Avatar>
        ))}
      </Box>
    </Box>
  );
}
